import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, Star, Share2, Eye, File, FileText, Video as VideoIcon, Music as MusicIcon, Monitor, Archive, Image as ImageIcon } from 'lucide-react';
import { FileItem } from '../types';
import { formatFileSize, formatDate, isImageFile } from '../utils/fileUtils';
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import "@cyntler/react-doc-viewer/dist/index.css";

interface FilePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  file: FileItem | null;
  onToggleStar?: (id: string) => void;
  onShare?: (id: string) => void;
  isGlobalDarkMode?: boolean;
}

const docViewerConfig = {
  header: {
    disableHeader: true,
    disableFileName: true,
    retainURLParams: false
  },
  pdfVerticalScrollByDefault: true,
};

const docViewerTheme = {
  primary: "#18181b", // Background
  secondary: "#ffffff", // Primary accent (Icons)
  tertiary: "#27272a", // Secondary accent (Button Backgrounds)
  textPrimary: "#ffffff", // Main Text
  textSecondary: "#18181b", // Text on accent
  textTertiary: "#ffffff99", // Subdued text/icons
  disableThemeScrollbar: false,
};

export const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  isOpen,
  onClose,
  file,
  onToggleStar,
  onShare,
}) => {
  const [imageError, setImageError] = useState(false);

  // Zoom & Pan state
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const canPreviewImage = Boolean(file && isImageFile(file.mimeType) && file.url && !imageError);
  const isDocumentFile = (mimeType?: string) => {
    if (!mimeType) return false;
    const lower = mimeType.toLowerCase();
    return (
      lower.includes('pdf') ||
      lower.includes('document') || // word
      lower.includes('msword') ||
      lower.includes('spreadsheet') || // excel
      lower.includes('excel') ||
      lower.includes('presentation') || // powerpoint
      lower.includes('powerpoint') ||
      lower.includes('text/plain') ||
      lower.includes('csv')
    );
  };
  const canPreviewDoc = Boolean(file && isDocumentFile(file.mimeType) && file.url);
  const canPreviewSvg = Boolean(file && file.mimeType?.includes('svg') && file.url);
  const canPreview = canPreviewImage || canPreviewSvg || canPreviewDoc;

  const memoizedDocuments = React.useMemo(() => {
    return file && file.url ? [{ uri: file.url, fileName: file.name }] : [];
  }, [file?.url, file?.name]);

  const resetZoom = useCallback(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setIsDragging(false);
  }, []);

  const handleZoomWheel = (e: React.WheelEvent) => {
    if (canPreviewImage || canPreviewSvg) {
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoom(prev => Math.min(Math.max(1, prev + delta), 4));
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => setIsDragging(false);

  useEffect(() => {
    if (file) {
      setImageError(false);
      resetZoom();
    }
  }, [file?.id, resetZoom]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'r' || e.key === 'R') resetZoom();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, resetZoom]);

  if (!isOpen || !file) return null;

  const getFileIcon = (size = 20, className = "") => {
    if (!file.mimeType) return <File size={size} className={className} />;
    
    if (file.mimeType.includes('pdf')) return <FileText size={size} className={`${className} text-red-500`} />;
    if (file.mimeType.includes('document') || file.mimeType.includes('msword')) return <FileText size={size} className={`${className} text-blue-500`} />;
    if (file.mimeType.includes('spreadsheet') || file.mimeType.includes('excel')) return <FileText size={size} className={`${className} text-green-500`} />;
    if (file.mimeType.includes('presentation') || file.mimeType.includes('powerpoint')) return <Monitor size={size} className={`${className} text-orange-500`} />;
    if (file.mimeType.startsWith('video/')) return <VideoIcon size={size} className={`${className} text-indigo-500`} />;
    if (file.mimeType.startsWith('audio/')) return <MusicIcon size={size} className={`${className} text-purple-500`} />;
    if (file.mimeType.includes('zip') || file.mimeType.includes('rar') || file.mimeType.includes('archive')) return <Archive size={size} className={`${className} text-gray-500`} />;
    if (file.mimeType.startsWith('image/')) return <ImageIcon size={size} className={`${className} text-green-400`} />;
    
    return <File size={size} className={className} />;
  };

  return (
    <div className="fixed inset-0 bg-black/95 backdrop-blur-sm shadow-2xl z-[9999] flex flex-col animate-in fade-in duration-200">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black/40 text-white border-b border-white/10 z-10 transition-colors">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="drop-shadow-md">
            {canPreviewImage ? <ImageIcon size={24} className="text-green-400" /> : getFileIcon(24)}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-bold text-white/90 truncate">{file.name}</h2>
            <p className="text-[11px] text-white/50 tracking-wide font-medium">
              {file.size ? formatFileSize(file.size) : ''} • {formatDate(file.modifiedAt)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1 sm:gap-2 ml-4">
          {onToggleStar && (
            <button
              onClick={() => onToggleStar(file.id)}
              className={`p-2 rounded-full transition-all duration-200 ${
                file.starred
                  ? 'text-yellow-400 bg-yellow-400/10'
                  : 'text-white/60 hover:text-white hover:bg-white/10'
              }`}
              title={file.starred ? 'Remove from bookmarked' : 'Add to bookmarked'}
            >
              <Star size={18} fill={file.starred ? 'currentColor' : 'none'} />
            </button>
          )}

          {onShare && (
            <button
              onClick={() => onShare(file.id)}
              className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-full transition-all duration-200"
              title="Share"
            >
              <Share2 size={18} />
            </button>
          )}

          <a
            href={file.url}
            download={file.name}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-full transition-all duration-200"
            title="Download"
          >
            <Download size={18} />
          </a>

          <div className="w-px h-6 bg-white/20 mx-2" />

          <button
            onClick={onClose}
            className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-full transition-all duration-200"
            title="Close"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div 
        className="flex-1 flex items-center justify-center p-4 overflow-hidden relative"
        onWheel={handleZoomWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {canPreviewImage || canPreviewSvg || canPreviewDoc ? (
          <div className="w-full h-full flex items-center justify-center relative">
            {canPreviewSvg ? (
              <object
                data={file.url}
                type="image/svg+xml"
                className="max-w-full max-h-full object-contain select-none pointer-events-none"
                style={{ 
                  transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
                  transition: isDragging ? 'none' : 'transform 0.15s ease-out'
                }}
                aria-label={file.name}
              >
                <div className="flex flex-col items-center justify-center p-12 min-h-[400px] text-center text-white/60">
                  <div className="mb-6 opacity-30">
                    <FileText size={96} />
                  </div>
                  <p className="font-medium">SVG preview not available</p>
                </div>
              </object>
            ) : canPreviewImage ? (
              <img
                src={file.url}
                alt={file.name}
                style={{
                  transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
                  transition: isDragging ? 'none' : 'transform 0.15s ease-out',
                  cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                }}
                className="max-w-full max-h-full object-contain shadow-2xl rounded-sm animate-in fade-in duration-200 select-none pointer-events-none"
                draggable={false}
                onError={() => setImageError(true)}
                onLoad={() => setImageError(false)}
              />
            ) : (
              <div className="w-full h-full bg-white/5 backdrop-blur-sm rounded-lg overflow-y-auto border border-white/10 doc-viewer-wrapper">
                <style>{`
                  .doc-viewer-wrapper svg {
                    fill: #ffffff !important;
                    color: #ffffff !important;
                  }
                  .doc-viewer-wrapper path {
                    fill: #ffffff !important;
                  }
                  .doc-viewer-wrapper div:has(> .react-pdf__Document) {
                    background-color: transparent !important;
                    background-image: none !important;
                  }
                  .doc-viewer-wrapper .react-pdf__Page {
                    background-color: transparent !important;
                  }
                `}</style>
                <DocViewer
                  documents={memoizedDocuments}
                  pluginRenderers={DocViewerRenderers}
                  style={{ width: "100%", height: "100%" }}
                  config={docViewerConfig}
                  theme={docViewerTheme}
                />
              </div>
            )}
            
            {/* Zoom Level Indicator */}
            {zoom > 1 && !canPreviewDoc && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  resetZoom();
                }}
                className="absolute bottom-6 right-6 px-3 py-1.5 bg-black/60 hover:bg-black/80 text-white text-[10px] font-bold tracking-widest uppercase rounded-full backdrop-blur-md border border-white/10 transition-all hover:scale-105 active:scale-95 animate-in fade-in zoom-in duration-200 z-[10001]"
              >
                {Math.round(zoom * 100)}% · Reset
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center p-12 text-center text-white">
            <div className="mb-6 opacity-50 drop-shadow-2xl">
              {getFileIcon(96)}
            </div>
            <h3 className="text-xl font-bold mb-2 text-white/90">{file.name}</h3>
            <p className="text-white/50 text-sm mb-8 font-medium">
              {file.mimeType || 'Unknown file type'}
            </p>

            <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 w-full max-w-sm border border-white/10 shadow-2xl">
              <h4 className="text-xs font-bold uppercase tracking-widest text-white/40 mb-4 border-b border-white/5 pb-2">File Information</h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-white/40">Size</span>
                  <span className="text-white/90 font-medium">{file.size ? formatFileSize(file.size) : 'Unknown'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/40">Type</span>
                  <span className="text-white/90 font-medium truncate max-w-[180px] text-right">{file.mimeType || 'Unknown'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/40">Created</span>
                  <span className="text-white/90 font-medium">{formatDate(file.createdAt)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/40">Modified</span>
                  <span className="text-white/90 font-medium">{formatDate(file.modifiedAt)}</span>
                </div>
              </div>
            </div>

            <div className="mt-8 flex items-center gap-2 text-white/30 text-xs font-bold tracking-widest uppercase">
              <Eye size={14} />
              Preview not available
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
